// Compare any two groups of your trades side by side (two accounts, two months, NQ vs ES,
// micros vs minis, the New York open vs the overnight session, two plays…): headline numbers,
// the biggest differences in plain words, both equity lines and the same breakdowns as Insights.
// "Your best" ranks every way of slicing your trades. Admin-only preview for now (see Journal.js).
import { useEffect, useMemo, useRef, useState } from 'react';
import { compareAccounts, equityLines } from './accountCompare';
import { COMPARE_TYPES, PERIOD_UNITS, choicesFor, equityByTradingDay } from './compareSlices';
import { ALL_TIME, DateRangeButton, rangeBounds } from './TradeToolbar';
import { api } from './api';
import { tradingDayKey } from './pnl';
import { formatMoney, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const STORE_KEY = 'mm-compare';

function readStored() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY)) || {};
  } catch {
    return {};
  }
}

function writeStored(value) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(value));
  } catch {
    // Not saved; the page still works.
  }
}

const pnlClass = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');

// Headline rows: how to show each number, and which way is better.
const METRICS = [
  { key: 'net', label: 'Net P&L', fmt: 'pnl', better: 'high' },
  { key: 'perTrade', label: 'Net per trade', fmt: 'pnl', better: 'high' },
  { key: 'perDay', label: 'Net per trading day', fmt: 'pnl', better: 'high' },
  { key: 'winRate', label: 'Win rate', fmt: 'pct', better: 'high' },
  { key: 'profitFactor', label: 'Profit factor', fmt: 'factor', better: 'high' },
  { key: 'avgWin', label: 'Average win', fmt: 'money', better: 'high' },
  { key: 'avgLoss', label: 'Average loss', fmt: 'loss', better: 'low' },
  { key: 'largestLoss', label: 'Largest loss', fmt: 'loss', better: 'low' },
  { key: 'maxDrawdown', label: 'Biggest drawdown', fmt: 'loss', better: 'low' },
  { key: 'greenDays', label: 'Green days', fmt: 'pct', better: 'high' },
  { key: 'count', label: 'Trades', fmt: 'int' },
  { key: 'days', label: 'Trading days', fmt: 'int' },
  { key: 'tradesPerDay', label: 'Trades per day', fmt: 'dec' },
  { key: 'fees', label: 'Fees', fmt: 'money' },
  { key: 'avgHold', label: 'Average hold', fmt: 'hold' },
];

function show(value, fmt, t) {
  if (value === null || value === undefined) return '—';
  if (fmt === 'pnl') return formatMoney(value);
  if (fmt === 'loss') return value ? formatMoney(-value) : usd.format(0);
  if (fmt === 'money') return usd.format(value);
  if (fmt === 'pct') return `${Math.round(value)}%`;
  if (fmt === 'factor') return value === Infinity ? '∞' : value.toFixed(2);
  if (fmt === 'dec') return value.toFixed(1);
  if (fmt === 'hold') return value < 60 ? t('{n} min', { n: Math.round(value) }) : t('{n} h', { n: (value / 60).toFixed(1) });
  return String(value);
}

function winner(metric, a, b) {
  if (!metric.better || a === null || b === null || a === undefined || b === undefined || a === b) return null;
  return (metric.better === 'high' ? a > b : a < b) ? 'a' : 'b';
}

function Scorecard({ a, b, names }) {
  const { t } = useI18n();
  return (
    <div className="table-wrap compare-score-wrap">
      <table className="compare-table compare-score">
        <thead>
          <tr>
            <th scope="col"><span className="visually-hidden">{t('Number')}</span></th>
            <th scope="col"><span className="compare-key compare-a" aria-hidden="true" />{names.a}</th>
            <th scope="col"><span className="compare-key compare-b" aria-hidden="true" />{names.b}</th>
          </tr>
        </thead>
        <tbody>
          {METRICS.map((m) => {
            const win = winner(m, a[m.key], b[m.key]);
            const cell = (side, v) => (
              <td className={`${m.fmt === 'pnl' ? pnlClass(v) : ''}${win === side ? ' is-better' : ''}`}>
                {show(v, m.fmt, t)}
                {win === side && <span className="compare-better" title={t('better')}><span className="compare-better-text">{t('better')}</span></span>}
              </td>
            );
            return (
              <tr key={m.key}>
                <th scope="row">{t(m.label)}</th>
                {cell('a', a[m.key])}
                {cell('b', b[m.key])}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Cumulative net P&L for both accounts, one point per trading day, with a hover readout.
function EquityChart({ points, names }) {
  const { t } = useI18n();
  const [hover, setHover] = useState(null);
  const box = useRef(null);
  if (points.length < 2) return <p className="empty">{t('Not enough trading days to draw a line yet.')}</p>;
  const W = 640;
  const H = 220;
  const pad = { l: 8, r: 8, t: 12, b: 12 };
  const values = points.flatMap((p) => [p.a, p.b, 0]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (i) => pad.l + (i / (points.length - 1)) * (W - pad.l - pad.r);
  const y = (v) => pad.t + (1 - (v - min) / span) * (H - pad.t - pad.b);
  const line = (k) => points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p[k]).toFixed(1)}`).join(' ');
  const last = points[points.length - 1];
  const fmtDate = (key) => (points[0].byIndex ? t('Trading day {n}', { n: key })
    : new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' }));

  function onMove(e) {
    const rect = box.current.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / rect.width;
    setHover(Math.max(0, Math.min(points.length - 1, Math.round(frac * (points.length - 1)))));
  }

  const h = hover === null ? null : points[hover];
  return (
    <div className="compare-chart">
      <div className="compare-legend">
        <span><span className="compare-key compare-a" aria-hidden="true" />{names.a} <strong className={pnlClass(last.a)}>{formatMoney(last.a)}</strong></span>
        <span><span className="compare-key compare-b" aria-hidden="true" />{names.b} <strong className={pnlClass(last.b)}>{formatMoney(last.b)}</strong></span>
      </div>
      <div className="compare-chart-box" ref={box} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
          aria-label={t('Cumulative net P&L: {a} {x}, {b} {y}', { a: names.a, x: formatMoney(last.a), b: names.b, y: formatMoney(last.b) })}>
          <line x1={pad.l} x2={W - pad.r} y1={y(0)} y2={y(0)} className="compare-zero" />
          <path d={line('a')} className="compare-line compare-line-a" />
          <path d={line('b')} className="compare-line compare-line-b" />
          {h && <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={H - pad.b} className="compare-cross" />}
        </svg>
        {h && (
          <div className={`compare-tip${hover > points.length / 2 ? ' left' : ''}`} style={{ left: `${(x(hover) / W) * 100}%` }}>
            <strong>{fmtDate(h.key)}</strong>
            <span><span className="compare-key compare-a" aria-hidden="true" />{names.a}: <span className={pnlClass(h.a)}>{formatMoney(h.a)}</span> <small>({formatMoney(h.dayA)} {t('that day')})</small></span>
            <span><span className="compare-key compare-b" aria-hidden="true" />{names.b}: <span className={pnlClass(h.b)}>{formatMoney(h.b)}</span> <small>({formatMoney(h.dayB)} {t('that day')})</small></span>
          </div>
        )}
      </div>
      <div className="compare-chart-dates"><span>{fmtDate(points[0].key)}</span><span>{fmtDate(last.key)}</span></div>
    </div>
  );
}

function SideCells({ row, other }) {
  if (!row || !row.count) return <><td className="compare-none">—</td><td /><td /></>;
  const better = other && other.count && row.net > other.net;
  return (
    <>
      <td>{row.count}</td>
      <td className={`${pnlClass(row.net)}${better ? ' is-better' : ''}`}>{formatMoney(row.net)}</td>
      <td>{Math.round(row.winRate)}%</td>
    </>
  );
}

function Section({ section, names }) {
  const { t } = useI18n();
  return (
    <section className={`insight-panel${section.id === 'time' ? ' wide' : ''}`}>
      <h2>{section.title}</h2>
      {section.sub && <p className="admin-panel-sub">{section.sub}</p>}
      {section.rows.length ? (
        <div className="table-wrap">
          <table className="compare-table compare-breakdown">
            <thead>
              <tr>
                <th scope="col" rowSpan={2}><span className="visually-hidden">{t('Group')}</span></th>
                <th scope="colgroup" colSpan={3} className="compare-group-a"><span className="compare-key compare-a" aria-hidden="true" />{names.a}</th>
                <th scope="colgroup" colSpan={3} className="compare-group-b"><span className="compare-key compare-b" aria-hidden="true" />{names.b}</th>
              </tr>
              <tr>
                <th scope="col">{t('Trades')}</th><th scope="col">{t('Net')}</th><th scope="col">{t('Win')}</th>
                <th scope="col" className="compare-group-b">{t('Trades')}</th><th scope="col">{t('Net')}</th><th scope="col">{t('Win')}</th>
              </tr>
            </thead>
            <tbody>
              {section.rows.map((r) => (
                <tr key={String(r.key)}>
                  <th scope="row">{r.label}</th>
                  <SideCells row={r.a} other={r.b} />
                  <SideCells row={r.b} other={r.a} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="empty">{section.id === 'setup' || section.id === 'mistake' ? t('No tagged trades in either account yet.') : t('Not enough trades yet.')}</p>
      )}
    </section>
  );
}

const GAP_LABELS = {
  count: 'Number of trades',
  winRate: 'Win rate',
  winSize: 'Size of wins',
  lossSize: 'Size of losses',
  fees: 'Fees',
};

// Where the dollar difference comes from: five parts that add up to the whole gap.
function GapPanel({ gap, names }) {
  const { t } = useI18n();
  const { a, b } = gap;
  const side = (v) => (v >= 0 ? 'a' : 'b');
  // Each account's value behind the line, e.g. "Topstep 36% · Apex 44%".
  const each = (fn) => [fn(a), fn(b)];
  const detail = {
    count: each((x) => String(x.n)),
    winRate: each((x) => `${Math.round(x.w * 100)}%`),
    winSize: each((x) => t('{amount} average', { amount: usd.format(x.W) })),
    lossSize: each((x) => t('{amount} average', { amount: formatMoney(-x.L) })),
    fees: each((x) => t('{amount} per trade', { amount: usd.format(x.f) })),
  };
  const items = [...gap.items].sort((x, y) => Math.abs(y.value) - Math.abs(x.value));
  const max = Math.max(1, ...items.map((i) => Math.abs(i.value)));
  const lead = side(gap.total);
  const biggest = items[0];
  return (
    <section className="insight-panel compare-gap">
      <h2>{t('Where the gap comes from')}</h2>
      <p className="compare-gap-lead">
        {Math.abs(gap.total) < 0.005
          ? t('Both accounts are even.')
          : t('{name} is ahead by {amount}.', { name: names[lead], amount: usd.format(Math.abs(gap.total)) })}{' '}
        {Math.abs(biggest.value) >= 0.005 && t('The biggest piece is {part}: {amount} in favor of {name}.',
          { part: t(GAP_LABELS[biggest.id]).toLowerCase(), amount: usd.format(Math.abs(biggest.value)), name: names[side(biggest.value)] })}
      </p>
      <ul className="compare-gap-list">
        {items.map((i) => (
          <li key={i.id}>
            <div className="compare-gap-label">
              <strong>{t(GAP_LABELS[i.id])}</strong>
              <small>{names.a} {detail[i.id][0]} &middot; {names.b} {detail[i.id][1]}</small>
            </div>
            <div className="compare-gap-bar" aria-hidden="true">
              <span className={`compare-gap-fill compare-fill-${side(i.value)}`} style={{ width: `${(Math.abs(i.value) / max) * 100}%` }} />
            </div>
            <div className="compare-gap-value">
              {Math.abs(i.value) < 0.005 ? '—' : <>{usd.format(Math.abs(i.value))} <small>{t('for {name}', { name: names[side(i.value)] })}</small></>}
            </div>
          </li>
        ))}
      </ul>
      <p className="insight-caution">{t('Wins and losses are measured before fees here, so fees get a line of their own. The five lines add up to the whole difference.')}</p>
    </section>
  );
}

// Each account's trades replayed with one change.
function WhatIfColumn({ result, name, keyClass }) {
  const { t } = useI18n();
  return (
    <div className="compare-whatif-col">
      <h3><span className={`compare-key ${keyClass}`} aria-hidden="true" />{name} <span className={pnlClass(result.base)}>{formatMoney(result.base)}</span></h3>
      {result.scenarios.length ? (
        <ul className="compare-whatif-list">
          {result.scenarios.map((sc) => (
            <li key={sc.id}>
              <div>
                <strong>{sc.label}</strong>
                <small>
                  {sc.removed ? t(sc.removed === 1 ? '{n} trade left out' : '{n} trades left out', { n: sc.removed }) : null}
                  {sc.removed && sc.note ? ' · ' : null}
                  {sc.note}
                </small>
              </div>
              <div className="compare-whatif-result">
                <span className={pnlClass(sc.net)}>{formatMoney(sc.net)}</span>
                <small className={pnlClass(sc.change)}>{formatMoney(sc.change)}</small>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="empty">{t('Nothing to replay yet: no mistake tags, losing streaks or outsized losses.')}</p>
      )}
    </div>
  );
}

function WhatIfPanel({ whatIf, names }) {
  const { t } = useI18n();
  return (
    <section className="insight-panel compare-whatif">
      <h2>{t('What if')}</h2>
      <p className="admin-panel-sub">{t('Your own past trades, replayed with one change. The first number is the new net P&L, the second is the difference.')}</p>
      <div className="compare-whatif-grid">
        <WhatIfColumn result={whatIf.a} name={names.a} keyClass="compare-a" />
        <WhatIfColumn result={whatIf.b} name={names.b} keyClass="compare-b" />
      </div>
      <p className="insight-caution">{t('A replay, not a prediction: a rule that looks good in hindsight still needs testing in live trading.')}</p>
    </section>
  );
}

// Which accounts one side of the comparison looks at.
function ScopeSelect({ value, onChange, accounts }) {
  const { t } = useI18n();
  return (
    <select className="compare-scope" value={value} onChange={(e) => onChange(e.target.value)} aria-label={t('Accounts')}>
      <option value="all">{t('In all accounts')}</option>
      {accounts.map((a) => <option key={a._id} value={a._id}>{t('In {name}', { name: a.name })}</option>)}
    </select>
  );
}

// Credit for the volatility levels (FRED asks for it).
function VixCredit({ context }) {
  const { t } = useI18n();
  if (!context) return null;
  return (
    <p className="insight-caution">
      {context.failed ? t('Market volatility couldn’t load right now. Try again later.')
        : t('Market volatility is based on the closing level of the CBOE Volatility Index (VIX) each day, retrieved from FRED, Federal Reserve Bank of St. Louis.')}
    </p>
  );
}

export default function ComparePage({ accounts, trades, library, shownIds, plays = [] }) {
  const { t, lang } = useI18n();
  const stored = readStored();
  const [type, setType] = useState(() => (COMPARE_TYPES.some(([k]) => k === stored.type) ? stored.type : 'accounts'));
  const [unit, setUnit] = useState(() => (PERIOD_UNITS.some(([k]) => k === stored.unit) ? stored.unit : 'month'));
  const [picks, setPicks] = useState(() => stored.picks || {});
  const [range, setRangeState] = useState(() => (stored.range && stored.range.preset ? stored.range : ALL_TIME));
  const remember = (patch) => writeStored({ ...readStored(), ...patch });
  const [showBreakdowns, setShowBreakdowns] = useState(Boolean(stored.breakdowns));
  const setRange = (r) => { setRangeState(r); remember({ range: r }); };
  // Which accounts each side looks at: all of them, or one. Starts on the account picked in the side menu.
  const firstScope = shownIds && shownIds.length === 1 ? shownIds[0] : 'all';
  const [scopes, setScopes] = useState(() => {
    const ok = (id) => id === 'all' || accounts.some((a) => a._id === id);
    const saved = stored.scopes || [];
    return [ok(saved[0]) ? saved[0] : firstScope, ok(saved[1]) ? saved[1] : firstScope];
  });
  const setScope = (i, v) => { const next = i ? [scopes[0], v] : [v, scopes[1]]; setScopes(next); remember({ scopes: next }); };
  const inScope = (scope) => (t) => scope === 'all' || t.accountId === scope;

  // News days and market volatility (marketCalendar.js), loaded when a comparison needs them.
  const [context, setContext] = useState(null);
  const needsContext = type === 'news' || type === 'volatility';
  useEffect(() => {
    if (!needsContext || context) return;
    api('/market-context').then(setContext).catch(() => setContext({ news: {}, volatility: {}, failed: true }));
  }, [needsContext, context]);

  const bounds = rangeBounds(range);
  const base = useMemo(() => trades.filter((x) => {
    if (!bounds) return true;
    const k = tradingDayKey(x.date);
    return k >= bounds.from && k <= bounds.to;
  }), [trades, bounds && bounds.from, bounds && bounds.to]); // eslint-disable-line react-hooks/exhaustive-deps
  const choices = useMemo(() => choicesFor(type, base, { accounts, plays, library, unit, context }), [type, base, accounts, plays, library, unit, context, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickKey = type === 'periods' ? `periods-${unit}` : type;
  const saved = picks[pickKey] || [];
  const has = (id) => choices.some((c) => c.id === id);
  const idA = has(saved[0]) ? saved[0] : choices[0] ? choices[0].id : '';
  const idB = has(saved[1]) ? saved[1] : (choices.find((c) => c.id !== idA) || {}).id || '';
  const cA = choices.find((c) => c.id === idA);
  const cB = choices.find((c) => c.id === idB);
  const choose = (a, b) => { const next = { ...picks, [pickKey]: [a, b] }; setPicks(next); remember({ picks: next }); };

  const scopeA = type === 'accounts' ? 'all' : scopes[0];
  const scopeB = type === 'accounts' ? 'all' : scopes[1];
  const tradesA = useMemo(() => (cA ? base.filter(inScope(scopeA)).filter(cA.test) : []), [base, cA, scopeA]); // eslint-disable-line react-hooks/exhaustive-deps
  const tradesB = useMemo(() => (cB ? base.filter(inScope(scopeB)).filter(cB.test) : []), [base, cB, scopeB]); // eslint-disable-line react-hooks/exhaustive-deps
  const accountName = (scope) => (scope === 'all' ? t('All accounts') : (accounts.find((a) => a._id === scope) || {}).name || '');
  // When the two sides look at different accounts, the account is part of each name.
  const withAccount = scopeA !== scopeB;
  const names = {
    a: cA ? (withAccount ? `${cA.label} · ${accountName(scopeA)}` : cA.label) : t('A'),
    b: cB ? (withAccount ? `${cB.label} · ${accountName(scopeB)}` : cB.label) : t('B'),
  };
  // The same choice twice is fine when the accounts differ (NQ in one account vs NQ in another).
  const ready = cA && cB && (idA !== idB || scopeA !== scopeB);
  const result = useMemo(() => (ready && tradesA.length && tradesB.length ? compareAccounts(tradesA, tradesB, { names, library }) : null),
    [ready, tradesA, tradesB, names.a, names.b, library, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const points = useMemo(() => (!result ? [] : type === 'periods' ? equityByTradingDay(tradesA, tradesB) : equityLines(tradesA, tradesB)), [result, type, tradesA, tradesB]);

  const options = choices.map((c) => <option key={c.id} value={c.id}>{c.label} ({c.n})</option>);

  return (
    <div className="compare-page">
      <div className="compare-head">
        <p className="subtitle">{t('Put any two parts of your trading side by side: accounts, weeks or months, instruments, sessions, plays, tags, news days and more.')}</p>
        <DateRangeButton range={range} setRange={setRange} />
      </div>

      {(
        <>
          <div className="compare-pickers compare-pickers-type">
            <label className="compare-picker">
              <span>{t('Compare')}</span>
              <select value={type} onChange={(e) => { setType(e.target.value); remember({ type: e.target.value }); }}>
                {COMPARE_TYPES.map(([k, label]) => <option key={k} value={k}>{t(label)}</option>)}
              </select>
            </label>
            {type === 'periods' && (
              <label className="compare-picker">
                <span>{t('By')}</span>
                <select value={unit} onChange={(e) => { setUnit(e.target.value); remember({ unit: e.target.value }); }}>
                  {PERIOD_UNITS.map(([k, label]) => <option key={k} value={k}>{t(label)}</option>)}
                </select>
              </label>
            )}
          </div>

          {choices.length < 2 ? (
            <p className="empty">{type === 'accounts' ? t('Add a second account to compare two accounts side by side.') : t('You need trades in at least two of these to compare them. Try another comparison or a longer period.')}</p>
          ) : (
            <>
              <div className="compare-pickers">
                <div className="compare-side">
                  <label className="compare-picker">
                    <span><span className="compare-key compare-a" aria-hidden="true" />A</span>
                    <select value={idA} onChange={(e) => choose(e.target.value, idB)}>{options}</select>
                  </label>
                  {type !== 'accounts' && <ScopeSelect value={scopes[0]} onChange={(v) => setScope(0, v)} accounts={accounts} />}
                </div>
                <button type="button" className="secondary small compare-swap" onClick={() => choose(idB, idA)} aria-label={t('Swap')} title={t('Swap')}>
                  &#8644;
                </button>
                <div className="compare-side">
                  <label className="compare-picker">
                    <span><span className="compare-key compare-b" aria-hidden="true" />B</span>
                    <select value={idB} onChange={(e) => choose(idA, e.target.value)}>{options}</select>
                  </label>
                  {type !== 'accounts' && <ScopeSelect value={scopes[1]} onChange={(v) => setScope(1, v)} accounts={accounts} />}
                </div>
              </div>

              {!ready ? (
                <p className="empty">{t('Pick two different ones, or the same one in two different accounts.')}</p>
              ) : !result ? (
                <p className="empty">{t('{name} has no trades in this period. Pick a longer period or another one.', { name: !tradesA.length ? names.a : names.b })}</p>
              ) : (
                <>
                  <div className="compare-top">
                    <section className="insight-panel">
                      <h2>{t('Side by side')}</h2>
                      <Scorecard a={result.a} b={result.b} names={names} />
                    </section>
                    <section className="insight-panel">
                      <h2>{t('What’s different')}</h2>
                      {result.findings.length ? (
                        <ul className="compare-findings">
                          {result.findings.map((f) => (
                            <li key={f.text}>
                              <span className="finding-area">{f.area}{f.small && <span className="finding-small">{t('Early observation')}</span>}</span>
                              {f.text}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="empty">{t('No big differences yet. They need at least 3 trades each in the same group to show up here.')}</p>
                      )}
                      <p className="insight-caution">{t('These are differences in your own trades, not proof of what caused them. Small groups can change quickly.')}</p>
                    </section>
                  </div>

                  <div className="compare-top">
                    <GapPanel gap={result.gap} names={names} />
                    <WhatIfPanel whatIf={result.whatIf} names={names} />
                  </div>

                  <section className="insight-panel compare-equity">
                    <h2>{t('Cumulative net P&L')}</h2>
                    <EquityChart points={points} names={names} />
                  </section>

                  <div className="compare-breakdowns-toggle">
                    <button type="button" className="secondary small" aria-expanded={showBreakdowns} onClick={() => { setShowBreakdowns((v) => !v); remember({ breakdowns: !showBreakdowns }); }}>
                      {showBreakdowns ? t('Hide the breakdowns') : t('Show the breakdowns (time, day, instrument, setups…)')}
                    </button>
                  </div>
                  {showBreakdowns && (
                    <div className="insight-panels">
                      {result.sections.map((sec) => <Section key={sec.id} section={sec} names={names} />)}
                    </div>
                  )}
                  {type === 'volatility' && <VixCredit context={context} />}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
